import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Spinner } from 'react-bootstrap';
import XGolifeDashboardComponent from './xgolife/components/XGolifeDashboardComponent'; 
import Swal from 'sweetalert2';
import AsyncStorage from '@react-native-async-storage/async-storage'; 
import { API_URL } from './config';

const AddUser = () => {
  const [userData, setUserData] = useState({
    company_id: "",
    branch_id: "",
    username: "",
    password: "",
    role: "",
    category: "",
    email: "",
    notify: "Yes",
    addproperty: "No",
    editproperty: "No",
    approverequests: "No",
    delivery: "No",
    status: "Active",
    client_profile_id: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/companysetup`);
      const data = await response.json();
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch companies',
        icon: 'error'
      });
    }
  };

  const fetchBranches = async (companyId) => {
    try {
      const response = await fetch(`${API_URL}/branch/company/${companyId}`);
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch branches for this company',
        icon: 'error'
      });
    }
  };

  const handleCompanyChange = (e) => {
    const selectedCompanyId = e.target.value;
    setUserData(prev => ({
      ...prev,
      company_id: selectedCompanyId,
      branch_id: "", // Reset branch_id when company changes
    }));
    fetchBranches(selectedCompanyId);
  };

  const handleBranchChange = (e) => {
    const selectedBranchId = e.target.value;
    setUserData(prev => ({
      ...prev,
      branch_id: selectedBranchId,
    }));
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!userData.username || !userData.email || !userData.password || !userData.company_id || !userData.branch_id || !userData.role) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields',
        icon: 'error'
      });
      setIsLoading(false);
      return;
    }

    // Log user data before posting
    console.log('User Data being posted:', userData);

    try {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error('Failed to create user');
      }

      Swal.fire({
        title: 'Success!',
        text: 'User created successfully',
        icon: 'success'
      });

      // Reset form
      setUserData({
        company_id: "",
        branch_id: "",
        username: "",
        password: "",
        role: "",
        category: "",
        email: "",
        notify: "Yes",
        addproperty: "No",
        editproperty: "No",
        approverequests: "No",
        delivery: "No",
        status: "Active",
        client_profile_id: "",
      });

    } catch (error) {
      console.error('Error creating user:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message,
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <XGolifeDashboardComponent>
      <div className="create-user-container">
        <Card className="shadow-sm">
          <Card.Header 
            className="bg-purple text-white py-3"
            style={{ backgroundColor: '#B03F82' }}
          >
            <h5 className="mb-0">Create New User</h5>
          </Card.Header>

          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              {/* Company Selection */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Select Company</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company*</Form.Label>
                      <Form.Select
                        name="company_id"
                        value={userData.company_id}
                        onChange={handleCompanyChange}
                        required
                      >
                        <option value="">Select a company</option>
                        {companies.map(company => (
                          <option key={company.company_id} value={company.company_id}>
                            {company.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Branch Selection */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Select Branch</h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Branch*</Form.Label>
                      <Form.Select
                        name="branch_id"
                        value={userData.branch_id}
                        onChange={handleBranchChange}
                        required
                      >
                        <option value="">Select a branch</option>
                        {branches.map(branch => (
                          <option key={branch.branch_id} value={branch.branch_id}>
                            {branch.branch_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* User Information */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">User Information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Username*</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={userData.username}
                        onChange={handleChange}
                        required
                        placeholder="Enter username"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email*</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={userData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password*</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={userData.password}
                        onChange={handleChange}
                        required
                        placeholder="Enter password"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Role*</Form.Label>
                      <Form.Select
                        name="role"
                        value={userData.role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select a role</option>
                        <option value="ecosystem_guest">Ecosystem Guest</option>
                        <option value="ecosystem_admin">Ecosystem Admin</option>
                        <option value="ecosystem_operator">Ecosystem Operator</option>
                        <option value="company_admin">Company Admin</option>
                        <option value="company_guest">Company Guest</option>
                        <option value="company_dispatch_operator">Company Dispatch Operator</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Notification Settings */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Notification Settings</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Notify</Form.Label>
                      <Form.Select
                        name="notify"
                        value={userData.notify}
                        onChange={handleChange}
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              {/* Permissions */}
              <section className="mb-4" style={{textAlign: "start"}}>
                <h6 className="mb-3 text-purple fw-bold">Permissions</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="addproperty"
                        checked={userData.addproperty === "Yes"}
                        onChange={handleChange}
                        label="Add Property"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="editproperty"
                        checked={userData.editproperty === "Yes"}
                        onChange={handleChange}
                        label="Edit Property"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="approverequests"
                        checked={userData.approverequests === "Yes"}
                        onChange={handleChange}
                        label="Approve Requests"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </section>

              <div className="d-flex justify-content-end mt-4">
                <Button 
                  variant="secondary" 
                  className="me-2"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  style={{ backgroundColor: '#B03F82', borderColor: '#B03F82' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <style jsx>{`
        .text-purple {
          color: #B03F82;
        }
        .create-user-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .form-control:focus, .form-select:focus {
          border-color: #B03F82;
          box-shadow: 0 0 0 0.25rem rgba(111, 66, 193, 0.25);
        }
      `}</style>
    </XGolifeDashboardComponent>
  );
};

export default AddUser;